/**
 * SettingsView - Application settings orchestrator
 *
 * ── Region glossary (shared vocabulary — use these names everywhere) ──
 *   settings-navigation  The left column of section links (Shop Profile,
 *                         Users, Products …). Pure navigation.
 *   settings-panel        The primary working area to its right — the region
 *                         that hosts the active section's *Panel component
 *                         (ProductsPanel, UsersPanel …). "panel" = the region;
 *                         a `*Panel` = the swappable thing rendered inside it.
 *   settings-breakout     A secondary region beside/below the panel for
 *                         drill-in or supplemental controls (e.g. the community
 *                         detail in wide mode). Currently only populated in wide
 *                         mode; narrow-mode breakout is the next thing we build.
 *   (settings-supplemental-pane — the full-bleed map; specific to Access Codes,
 *    not part of the three-region vocabulary above.)
 *
 * Two layout modes depending on the active section's widthMode:
 *
 *   narrow  → settings-navigation + settings-panel (max-w-3xl), padded page.
 *
 *   wide (Access Codes)  → full-screen map with floating regions:
 *     - settings-navigation (w-56) sits at the outer level, as in narrow mode.
 *     - settings-supplemental-pane (the map) fills the area edge-to-edge.
 *     - settings-panel (community list) floats top-left, max-h 40vh, scrollable.
 *     - settings-breakout (community detail) stacks below the panel when a
 *       community is selected; hidden otherwise.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Store, KeyRound, Plug, Sliders, Package, Flower2, Lock, Monitor, HardDrive } from 'lucide-react';

import {
  ShopProfilePanel,
  UsersPanel,
  IntegrationsPanel,
  PreferencesPanel,
  ProductsPanel,
  SendingFloristsPanel,
  AccessCodesPanel,
  DevicesPanel,
  ThisDevicePanel,
  PortableTransferPanel,
} from '@/components/settings';
import CommunityDetailPanel from '@/components/settings/CommunityDetailPanel';
import CommunityMap from '@/components/settings/CommunityMap';
import { useCommunityWorkspace } from '@/hooks/useCommunityWorkspace';
import { registerCommunityLeaveGuard } from '@/utils/communityNavigationGuard';
import { useAuth } from '@/context/AuthContext';
import {
  SETTINGS_ACCESS,
  allowedSettingsSections,
  hasSettingsCapability,
} from '@/utils/settingsAccess';

// ---------------------------------------------------------------------------
// Section registry
// ---------------------------------------------------------------------------
const SECTIONS = [
  { id: 'shop',         group: 'workspace', icon: Store,   label: 'Shop Profile',      description: 'Business name, address, timezone',              widthMode: 'narrow', requiredCapability: SETTINGS_ACCESS.READ },
  { id: 'users',        group: 'workspace', icon: KeyRound, label: 'Users',             description: 'Manage login accounts and drivers',             widthMode: 'narrow', requiredCapability: SETTINGS_ACCESS.MANAGE },
  { id: 'preferences',  group: 'workspace', icon: Sliders, label: 'Preferences',       description: 'Fuel costs, display settings',                  widthMode: 'narrow', requiredCapability: SETTINGS_ACCESS.MANAGE },
  { id: 'products',     group: 'data',      icon: Package, label: 'Products',           description: 'Catalog of product codes and details',          widthMode: 'narrow', requiredCapability: SETTINGS_ACCESS.MANAGE },
  { id: 'access-codes', group: 'data',      icon: Lock,    label: 'Access Codes',       description: 'Gate codes and community associations',         widthMode: 'wide', mobileDisabled: true, requiredCapability: SETTINGS_ACCESS.MANAGE },
  { id: 'florists',     group: 'data',      icon: Flower2, label: 'Sources & Senders', description: 'Manage order sources and sending florists',     widthMode: 'narrow', requiredCapability: SETTINGS_ACCESS.MANAGE },
  { id: 'integrations', group: 'system',    icon: Plug,    label: 'Integrations',      description: 'Stripe, Google Maps, and other services',       widthMode: 'narrow', requiredCapability: SETTINGS_ACCESS.MANAGE },
  { id: 'devices',      group: 'system',    icon: Monitor, label: 'Devices',           description: 'Registered desktop installs',                   widthMode: 'narrow', requiredCapability: SETTINGS_ACCESS.MANAGE },
  { id: 'this-device',  group: 'system',    icon: HardDrive, label: 'This Device',       description: 'What is stored here for working offline',      widthMode: 'narrow', desktopOnly: true, requiredCapability: SETTINGS_ACCESS.MANAGE },
  { id: 'portable-transfer', group: 'system', icon: Package, label: 'Portable Transfer', description: 'Move downloaded shop data between computers', widthMode: 'narrow', desktopOnly: true, requiredCapability: SETTINGS_ACCESS.PORTABLE_TRANSFER },
];

const GROUPS = [
  { id: 'workspace', label: 'Settings' },
  { id: 'data',      label: 'Data'      },
  { id: 'system',    label: 'System'    },
];

const DEFAULT_SETTINGS_SECTION = 'shop';

// Approximate height of the sticky nav bar (p-2 wrapper + py-2 links + border).
// Used to size the wide-mode container so it fills exactly the space below the nav.
const NAV_H = 57;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const SettingsView = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const portableDesktopAvailable = typeof window.lvbDesktop?.getPortableTransferStatus === 'function';
  // Each desktop-only section probes the bridge it actually uses. Portable
  // Transfer keeps its transfer probe; This Device needs the readiness bridge
  // and would be wrongly hidden by a transfer-specific test (TCA-26100804).
  const readinessDesktopAvailable = typeof window.lvbDesktop?.getMediaReadinessStatus === 'function';
  const visibleSections = useMemo(
    () => SECTIONS.filter((section) => {
      if (!section.desktopOnly) return true;
      if (section.id === 'portable-transfer') return portableDesktopAvailable;
      return readinessDesktopAvailable;
    }),
    [portableDesktopAvailable, readinessDesktopAvailable],
  );
  const permittedSections = useMemo(
    () => allowedSettingsSections(visibleSections, user),
    [visibleSections, user],
  );
  const permittedSectionIds = useMemo(
    () => new Set(permittedSections.map((section) => section.id)),
    [permittedSections],
  );
  const canManageSettings = hasSettingsCapability(user, SETTINGS_ACCESS.MANAGE);
  // Initial section honors authorized ?section= deep-links (e.g. from
  // AddressIntel). Shop Profile is the safe universal fallback.
  const initialSection = (() => {
    const requested = searchParams.get('section');
    return permittedSectionIds.has(requested) ? requested : DEFAULT_SETTINGS_SECTION;
  })();
  const [activeSection, setActiveSection] = useState(initialSection);
  // Narrow-mode settings-breakout: a contextual region beside the panel. Holds
  // whatever node the active panel opens (e.g. Products' size editor); null =
  // closed, so the panel sits alone at its comfortable width.
  const [breakout, setBreakout] = useState(null);
  const openBreakout = useCallback((node) => setBreakout(node), []);
  const closeBreakout = useCallback(() => setBreakout(null), []);
  const communityEnabled = activeSection === 'access-codes' && canManageSettings;
  const workspace = useCommunityWorkspace({ enabled: communityEnabled, canManage: canManageSettings });
  const workspaceRef = useRef(workspace);
  workspaceRef.current = workspace;
  const { selectedCommunityId, detail: communityData, pendingPins } = workspace;
  const [mapFitKey, setMapFitKey] = useState(0);

  useEffect(() => {
    if (!communityEnabled) return undefined;
    return registerCommunityLeaveGuard(() => workspaceRef.current.canLeave());
  }, [communityEnabled]);

  // Respond to subsequent ?section= / ?community= changes (e.g. a deep-link
  // clicked while the Settings page is already open). Strip the params after
  // consuming so refreshing the URL doesn't re-trigger.
  useEffect(() => {
    if (!permittedSectionIds.has(activeSection)) {
      setActiveSection(DEFAULT_SETTINGS_SECTION);
      setBreakout(null);
    }
    const reqSection = searchParams.get('section');
    const reqCommunity = searchParams.get('community');
    if (!reqSection && !reqCommunity) return;
    if (reqSection && permittedSectionIds.has(reqSection) && reqSection !== activeSection) {
      if (communityEnabled && !workspaceRef.current.canLeave()) {
        setSearchParams({}, { replace: true });
        return;
      }
      setActiveSection(reqSection);
      setBreakout(null);
      // A newly enabled workspace acquires its session before consuming its ID.
      if (reqSection === 'access-codes' && reqCommunity) return;
    }
    if (reqCommunity && reqSection === 'access-codes' && permittedSectionIds.has('access-codes')) {
      workspaceRef.current.selectCommunity(reqCommunity);
    }
    // Clear the params (replace, not push, to keep history clean).
    setSearchParams({}, { replace: true });
  }, [activeSection, communityEnabled, permittedSectionIds, searchParams, setSearchParams]);

  const handleSectionChange = (id) => {
    if (!permittedSectionIds.has(id)) return;
    if (id === activeSection) return;
    if (communityEnabled && !workspace.canLeave()) return;
    setActiveSection(id);
    setBreakout(null);   // close any open breakout when switching sections
  };

  const renderSection = () => {
    if (!permittedSectionIds.has(activeSection)) {
      return <ShopProfilePanel canManage={canManageSettings} />;
    }
    switch (activeSection) {
      case 'shop':         return <ShopProfilePanel canManage={canManageSettings} />;
      case 'users':        return <UsersPanel />;
      case 'products':     return <ProductsPanel onOpenBreakout={openBreakout} onCloseBreakout={closeBreakout} />;
      case 'access-codes': return (
        <AccessCodesPanel workspace={workspace} />
      );
      case 'florists':     return <SendingFloristsPanel onOpenBreakout={openBreakout} onCloseBreakout={closeBreakout} />;
      case 'integrations': return <IntegrationsPanel />;
      case 'devices':      return <DevicesPanel onOpenSection={(id) => setSearchParams({ section: id })} />;
      case 'this-device':  return <ThisDevicePanel />;
      case 'portable-transfer': return <PortableTransferPanel />;
      case 'preferences':  return <PreferencesPanel />;
      default:             return null;
    }
  };

  const activeConfig = permittedSections.find(s => s.id === activeSection)
    || permittedSections.find(s => s.id === DEFAULT_SETTINGS_SECTION);
  const isWide = activeConfig?.widthMode === 'wide';

  // ---------------------------------------------------------------------------
  // Shared: settings-navigation (render function to avoid component-in-component)
  // ---------------------------------------------------------------------------
  const renderSettingsNavigation = () => (
    <nav className="flex lg:flex-col gap-1 lg:gap-0 overflow-x-auto scrollbar-hide pb-2 lg:pb-0">
      {GROUPS.map(({ id: groupId, label: groupLabel }) => {
        const items = visibleSections.filter(s => s.group === groupId
          && (!s.desktopOnly || permittedSectionIds.has(s.id)));
        if (!items.length) return null;
        return (
          <div
            key={groupId}
            className="flex lg:flex-col gap-1 lg:gap-0 flex-shrink-0 lg:mb-3 last:lg:mb-0"
            data-testid={`settings-group-${groupId}`}
          >
            <div
              className="hidden lg:block px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-stone-400"
              data-testid={`settings-group-label-${groupId}`}
            >
              {groupLabel}
            </div>
            {items.map(({ id, icon: Icon, label, description, mobileDisabled }) => {
              const permitted = permittedSectionIds.has(id);
              return (
                <button
                  key={id}
                  onClick={() => { handleSectionChange(id); }}
                  disabled={!permitted}
                  aria-disabled={!permitted}
                  title={!permitted ? 'Administrator access required' : undefined}
                  className={[
                    'flex items-center gap-2 lg:gap-3 px-3 py-2 lg:py-2.5 rounded-lg text-left transition-colors whitespace-nowrap flex-shrink-0',
                    activeSection === id ? 'bg-primary/10 text-primary' : 'text-stone-600 hover:bg-stone-100',
                    !permitted ? 'opacity-40 cursor-not-allowed hover:bg-transparent' : '',
                    mobileDisabled && permitted ? 'lg:pointer-events-auto pointer-events-none opacity-40 lg:opacity-100' : '',
                  ].join(' ')}
                  data-testid={`settings-nav-${id}`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{label}</div>
                    {!permitted && (
                      <div className="text-[10px] text-stone-400">Admin only</div>
                    )}
                    {mobileDisabled && permitted && (
                      <div className="lg:hidden text-[10px] text-stone-400">Desktop only</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        );
      })}
    </nav>
  );

  // ---------------------------------------------------------------------------
  // Wide mode: full-screen map + floating panels (Access Codes)
  // ---------------------------------------------------------------------------
  if (isWide) {
    return (
      <div
        className="flex flex-row bg-stone-50 overflow-hidden"
        style={{ height: `calc(100vh - ${NAV_H}px)` }}
        data-testid="settings-view"
        data-testid2="settings-shell-wide"
      >
        {/* ── settings-navigation — outer level, same as narrow mode ───────── */}
        <div
          className="hidden lg:block w-56 flex-shrink-0 border-r border-stone-200 overflow-y-auto px-3 py-4"
          data-testid="settings-navigation"
        >
          {renderSettingsNavigation()}
        </div>

        {/* ── Content area — full-bleed map (supplemental) with floating
             settings-panel + settings-breakout on top ──────────────────────── */}
        <div className="flex-1 relative overflow-hidden min-w-0">

          {/* Map — fills the entire content area */}
          <div className="absolute inset-0" data-testid="settings-supplemental-pane">
            <CommunityMap
              addresses={communityData?.addresses || []}
              pendingPins={pendingPins}
              onPendingPinAdd={selectedCommunityId && workspace.canMutate ? workspace.addPendingPin : undefined}
              onPendingPinRemove={selectedCommunityId && workspace.canMutate ? workspace.removePendingPin : undefined}
              onSavedAddressRemove={selectedCommunityId && workspace.canMutate ? workspace.removeAddress : undefined}
              selectionKey={workspace.opening}
              captureSelection={workspace.captureSelection}
              isCurrentSelection={workspace.isCurrent}
              fitKey={mapFitKey}
              fullHeight
            />
          </div>

          {/* Stacked floating regions — settings-panel + settings-breakout, top-left */}
          <div className="absolute top-4 left-4 w-96 z-10 flex flex-col gap-4 max-h-[calc(100%-2rem)]">

            {/* settings-panel — capped at 40% screen height, scrollable, won't be squished by the breakout */}
            <div
              className="bg-white rounded-xl shadow-lg overflow-y-auto max-h-[40vh] flex-shrink-0"
              data-testid="settings-panel"
            >
              {renderSection()}
            </div>

            {/* settings-breakout — appears below the panel when a community is selected */}
            {selectedCommunityId && (
              <div
                className="bg-[#e8eeea] rounded-xl shadow-lg overflow-y-auto flex-1 min-h-0"
                data-testid="settings-breakout"
              >
                <CommunityDetailPanel
                  workspace={workspace}
                  showMap={false}
                  onFitMap={() => setMapFitKey((k) => k + 1)}
                />
              </div>
            )}

          </div>

        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Narrow mode: standard padded layout (all non-wide sections)
  // ---------------------------------------------------------------------------
  const panelCard = (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {activeConfig && <activeConfig.icon className="h-5 w-5" />}
          {activeConfig?.label}
        </CardTitle>
        <CardDescription>{activeConfig?.description}</CardDescription>
      </CardHeader>
      <CardContent>
        {renderSection()}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-stone-50" data-testid="settings-view">
      <div data-testid="settings-shell-narrow">
        <div className="flex flex-col lg:flex-row">
          {/* settings-navigation */}
          <div
            className="w-full lg:w-56 flex-shrink-0 px-3 py-4 lg:border-r lg:border-stone-200"
            data-testid="settings-navigation"
          >
            {renderSettingsNavigation()}
          </div>
          {/* Content area — settings-panel, plus settings-breakout when open.
              Equal flexible columns, each capped at the panel's reading width so
              they balance on big monitors and shrink together on small ones. */}
          <div className="flex-1 min-w-0 p-4 md:p-8 flex flex-col lg:flex-row items-start gap-6">
            <div className="w-full lg:flex-1 lg:max-w-3xl min-w-0" data-testid="settings-panel">
              {panelCard}
            </div>
            {breakout && (
              <div className="w-full lg:flex-1 lg:max-w-3xl min-w-0" data-testid="settings-breakout">
                {breakout}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsView;
